import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Toaster, toast } from 'sonner';
import { Gift, BookOpen, Loader2 } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import BookCover from '../components/BookCover';
import { loginWithGoogle } from '../utils/auth';

import { BACKEND_URL, API } from '../utils/api';
import { authFetch, clearToken } from '../utils/session';
const R2_URL = process.env.REACT_APP_R2_URL || BACKEND_URL + "/api/static";

const GiftPage = () => {
  const { giftId } = useParams();
  const navigate = useNavigate();
  const [gift, setGift] = useState(null);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [redeeming, setRedeeming] = useState(false);
  const [redeemed, setRedeemed] = useState(false);

  useEffect(() => {
    Promise.all([
      fetch(`${API}/regalo/${giftId}`).then(r => r.ok ? r.json() : null),
      authFetch(`${API}/auth/me`, {  }).then(r => r.ok ? r.json() : null),
    ]).then(([g, u]) => {
      setGift(g);
      setUser(u);
      if (g?.redeemed) setRedeemed(true);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [giftId]);

  const handleRedeem = async () => {
    if (!user) { loginWithGoogle(`/regalo/${giftId}`); return; }
    setRedeeming(true);
    const res = await fetch(`${API}/regalo/${giftId}/redeem`, { method: 'POST' });
    if (res.ok) {
      setRedeemed(true);
      toast.success('¡Libros agregados a tu biblioteca!');
    } else {
      toast.error('Error al abrir el regalo');
    }
    setRedeeming(false);
  };

  if (loading) return <div className="min-h-dvh flex items-center justify-center"><Loader2 className="size-6 animate-spin text-muted-foreground" /></div>;

  if (!gift) {
    return (
      <div className="min-h-dvh bg-background font-sans">
        <Header />
        <div className="text-center pt-20">
          <p className="text-muted-foreground">Este regalo no existe o ya fue usado</p>
          <button onClick={() => navigate('/')} className="mt-4 text-sm font-medium hover:underline">Ir a la tienda</button>
        </div>
      </div>
    );
  }

  // Enrich books with cover URLs
  const books = (gift.books || []).map(b => ({
    ...b,
    coverUrl: `${R2_URL}/books/${b.slug}/page_1.webp`,
  }));

  return (
    <div className="min-h-dvh bg-background font-sans">
      <Toaster position="top-center" />
      <Header />

      <div className="max-w-md mx-auto px-6 pt-12 pb-20 text-center">
        {redeemed ? (
          <>
            <div className="text-5xl mb-4">📖</div>
            <h1 className="text-2xl font-bold mb-2">¡Regalo abierto!</h1>
            <p className="text-sm text-muted-foreground mb-8">{gift.books.length} librito{gift.books.length > 1 ? 's' : ''} agregado{gift.books.length > 1 ? 's' : ''} a tu biblioteca</p>
            <div className="flex justify-center gap-3 mb-8">
              {books.map(b => (
                <div key={b.slug} className="w-20 h-[106px] rounded-lg overflow-hidden shadow-md">
                  <BookCover book={b} hideTitle />
                </div>
              ))}
            </div>
            <button onClick={() => navigate('/biblioteca')} className="h-11 px-6 rounded-xl bg-foreground text-background text-sm font-semibold hover:bg-foreground/90 transition-colors">
              <BookOpen className="size-4 inline mr-2" />Ir a Mi Biblioteca
            </button>
            <div className="mt-10 pt-8 border-t border-border">
              <p className="text-sm font-medium mb-2">¿Quieres regalar un librito tú también?</p>
              <p className="text-xs text-muted-foreground mb-4">Comparte la fe con los más pequeños</p>
              <button onClick={() => navigate('/')} className="h-10 px-5 rounded-xl border border-border text-sm font-medium hover:bg-secondary transition-colors">
                Ver libritos disponibles
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="text-5xl mb-4 animate-bounce">🎁</div>
            <h1 className="text-2xl font-bold mb-2">¡Alguien te regaló un librito!</h1>
            {gift.message && <p className="text-sm text-muted-foreground italic mb-2">"{gift.message}"</p>}
            <p className="text-xs text-muted-foreground/60 mb-8">De parte de {gift.buyer_name}</p>
            <div className="flex justify-center gap-3 mb-8">
              {books.map(b => (
                <div key={b.slug} className="w-24 h-[128px] rounded-lg overflow-hidden shadow-lg">
                  <BookCover book={b} hideTitle />
                </div>
              ))}
            </div>
            <p className="text-sm text-muted-foreground mb-4">{gift.books.length} librito{gift.books.length > 1 ? 's' : ''} cristiano{gift.books.length > 1 ? 's' : ''} ilustrado{gift.books.length > 1 ? 's' : ''}</p>
            <button onClick={handleRedeem} disabled={redeeming} className="h-12 px-8 rounded-xl bg-foreground text-background text-sm font-semibold hover:bg-foreground/90 transition-colors disabled:opacity-50">
              {redeeming ? <Loader2 className="size-4 animate-spin inline mr-2" /> : <Gift className="size-4 inline mr-2" />}
              {user ? 'Abrir mi regalo' : 'Iniciar sesión para abrir'}
            </button>
          </>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default GiftPage;
